/**
 * Storage Key Utilities
 *
 * Provides secure key generation for localStorage operations while respecting
 * SecureStorage's 100-character key length limit.
 */

/**
 * Creates a storage key that respects the 100-character limit while maintaining
 * entity isolation and collision resistance.
 *
 * Strategy:
 * 1. If the full key is under 90 characters (margin for safety), use it directly
 * 2. If too long, create a hash of the full key for collision resistance
 *
 * @param baseKey - The base key identifier (e.g., 'compliance-filters')
 * @param clientId - UUID client identifier
 * @param entityId - UUID entity identifier
 * @param suffix - Optional suffix (e.g., 'client-filters')
 * @returns A storage key under 100 characters that maintains entity isolation
 */
export function createEntityStorageKey(
  baseKey: string,
  clientId: string,
  entityId: string,
  suffix?: string
): string {
  // Build the full key with proper separation
  const parts = [baseKey, clientId, entityId];
  if (suffix) {
    parts.push(suffix);
  }
  const fullKey = parts.join('-');

  // If under the safe limit (90 chars for margin), use directly
  const SAFE_LIMIT = 90;
  if (fullKey.length <= SAFE_LIMIT) {
    return fullKey;
  }

  // Generate hash for collision resistance
  // Use simple base64 encoding of the full key, then truncate from the end
  // to capture the unique suffix parts
  const base64Hash = btoa(fullKey).replace(/[+/=]/g, ''); // Remove base64 special chars
  const hash = base64Hash.substring(Math.max(0, base64Hash.length - 16)); // Take last 16 chars

  // Create abbreviated key with hash
  const abbreviatedKey = `${baseKey}-${hash}`;

  // Ensure we're still under the limit
  if (abbreviatedKey.length > 95) {
    // Fallback: ultra-short format
    return `${baseKey.substring(0, 8)}-${hash}`;
  }

  return abbreviatedKey;
}

/**
 * Creates a storage key specifically for compliance filters with entity scoping
 */
export function createComplianceFiltersKey(
  clientId: string,
  entityId: string,
  filterType: 'client' | 'server' = 'client'
): string {
  const baseKey = 'comp-filters';
  const suffix = `${filterType}-filters`;
  return createEntityStorageKey(baseKey, clientId, entityId, suffix);
}

/**
 * Creates a storage key for compliance view mode with entity scoping
 */
export function createComplianceViewKey(
  clientId: string,
  entityId: string
): string {
  const baseKey = 'comp-view';
  return createEntityStorageKey(baseKey, clientId, entityId);
}

/**
 * Validates that a storage key meets SecureStorage requirements
 * @param key - The key to validate
 * @returns True if the key is valid for SecureStorage
 */
export function validateStorageKey(key: string): boolean {
  if (!key || typeof key !== 'string') {
    return false;
  }

  if (key.length > 100) {
    return false;
  }

  // Check for dangerous characters (same as SecureStorage validation)
  const dangerousPatterns = [/[<>]/g, /javascript:/gi, /data:/gi];
  return !dangerousPatterns.some(pattern => pattern.test(key));
}

/**
 * Legacy key migration helper - checks for old format keys and migrates data
 * @param oldKey - The old storage key format
 * @param newKey - The new storage key format
 * @returns The value from old key if found, null otherwise
 */
export function migrateStorageKey(oldKey: string, newKey: string): unknown {
  try {
    // Only attempt migration if old key exists and new key doesn't
    const oldValue = localStorage.getItem(oldKey);
    const newValue = localStorage.getItem(newKey);

    if (oldValue && !newValue) {
      // Migrate the data
      localStorage.setItem(newKey, oldValue);
      localStorage.removeItem(oldKey);
      console.debug(`[StorageKeyUtils] Migrated data from '${oldKey}' to '${newKey}'`);
      return JSON.parse(oldValue);
    }

    return null;
  } catch (error) {
    console.warn('[StorageKeyUtils] Failed to migrate storage key:', error);
    return null;
  }
}